import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { adminLiveData, getRecordingByLiveId } from '@/mocks/adminLive';
import type { AdminLive, LiveStatus, LiveProvider, AdminLiveRecording } from '@/mocks/adminLive';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { liveSessionService } from '@/services/liveSession';
import type { LiveSessionStatus } from '@/types/liveSession';
import { LIVE_SESSION_STATUS_LABELS, liveSessionStatusColor } from '@/types/liveSession';
import { getSupabaseClient } from '@/services/supabase/client';
import LiveGrid from './components/LiveGrid';
import LiveFormModal from './components/LiveFormModal';
import PostLiveFlowModal from './components/PostLiveFlowModal';
import type { PostLiveFlowResult } from './components/PostLiveFlowModal';
import NotificationFlowModal from '@/pages/admin/notifications/components/NotificationFlowModal';
import type { NotificationFlowContext } from '@/pages/admin/notifications/components/NotificationFlowModal';

// ══════════════════════════════════════════════════════════
// LIVE SESSION STATE — Estados transicionales por live
// ══════════════════════════════════════════════════════════

export interface LiveSessionState {
  sessionId: string;
  status: LiveSessionStatus;
  statusMessage: string;
  errorMessage: string | null;
  isStarting: boolean;
  durationSeconds: number | null;
  isLoading: boolean;
  startedAt: string | null;
}

// ══════════════════════════════════════════════════════════
// LOCALSTORAGE PERSISTENCE — Recuperar sesión tras refresh
// ══════════════════════════════════════════════════════════

const LS_SESSION_PREFIX = 'rfw_live_session_';

interface StoredSession {
  sessionId: string;
  liveId: number;
  startedAt: string;
}

function persistSession(liveId: number, data: StoredSession): void {
  try {
    localStorage.setItem(`${LS_SESSION_PREFIX}${liveId}`, JSON.stringify(data));
  } catch {
    // localStorage puede fallar en modo incógnito o cuota llena
  }
}

function removeStoredSession(liveId: number): void {
  try {
    localStorage.removeItem(`${LS_SESSION_PREFIX}${liveId}`);
  } catch {
    // no-op
  }
}

function getAllStoredSessions(): StoredSession[] {
  const sessions: StoredSession[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LS_SESSION_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed: unknown = JSON.parse(raw);
          const s = parsed as StoredSession;
          if (s.sessionId && s.liveId) {
            sessions.push(s);
          }
        }
      }
    }
  } catch {
    // no-op
  }
  return sessions;
}

const allProviders: LiveProvider[] = ['youtube', 'facebook', 'vps', 'mux'];

function showBlockAlert(onConfirm: () => void) {
  SwalAlert.warning(
    'Función Premium',
    'Las transmisiones en vivo son exclusivas del plan Premium. Actualizá tu plan para crear y gestionar lives.',
  ).then((result) => {
    if (result.isConfirmed) onConfirm();
  });
}

export default function AdminLivePage() {
  const { isPremium, isFree, planId } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  const [lives, setLives] = useState<AdminLive[]>([]);

  const [filterStatus, setFilterStatus] = useState<LiveStatus | 'todos'>('todos');
  const [filterProvider, setFilterProvider] = useState<LiveProvider | 'todos'>('todos');
  const [searchQuery, setSearchQuery] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingLive, setEditingLive] = useState<AdminLive | null>(null);

  const [pageState, setPageState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'transmision', relatedModule: 'live' },
  });

  const [postLiveModal, setPostLiveModal] = useState<{ open: boolean; live: AdminLive | null }>({
    open: false,
    live: null,
  });

  // ── LIVE SESSION STATE ──
  const [liveSessionStates, setLiveSessionStates] = useState<Record<number, LiveSessionState>>({});
  const pollingRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // ── ADMIN RADIO CONTEXT (cached) ──
  const adminRadioRef = useRef<{ radioId: string; stationId: string } | null>(null);

  const getAdminRadioContext = useCallback(async (): Promise<{ radioId: string; stationId: string } | null> => {
    if (adminRadioRef.current) return adminRadioRef.current;

    try {
      const supabase = getSupabaseClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!profile) return null;

      const { data: membership } = await supabase
        .from('radio_memberships')
        .select('radio_id')
        .eq('profile_id', profile.id)
        .limit(1)
        .maybeSingle();
      if (!membership) return null;

      const { data: station } = await supabase
        .from('stations')
        .select('id')
        .eq('radio_id', membership.radio_id)
        .limit(1)
        .maybeSingle();
      if (!station) return null;

      adminRadioRef.current = { radioId: membership.radio_id, stationId: station.id };
      return adminRadioRef.current;
    } catch {
      return null;
    }
  }, []);

  // ── Restoration guard (solo una vez) ──
  const restoredRef = useRef(false);

  // ── Cleanup polling on unmount ──
  useEffect(() => {
    const refs = pollingRefs.current;
    return () => {
      Object.values(refs).forEach(clearInterval);
    };
  }, []);

  // ── Restaurar sesiones activas desde localStorage tras refresh ──
  useEffect(() => {
    if (restoredRef.current) return;
    if (pageState !== 'ready') return;
    restoredRef.current = true;

    const stored = getAllStoredSessions();
    if (stored.length === 0) return;

    (async () => {
      for (const s of stored) {
        try {
          const updated = await liveSessionService.getLiveSession(s.sessionId);

          if (updated.status === 'completed' || updated.status === 'failed') {
            // Sesión ya terminó — limpiar localStorage y actualizar el estado del live
            removeStoredSession(s.liveId);
            if (updated.status === 'completed') {
              setLives((prev) =>
                prev.map((l) =>
                  l.id === s.liveId ? { ...l, status: 'finalizado' as LiveStatus } : l
                )
              );
            }
            continue;
          }

          // Sesión activa — restaurar estado
          let statusMessage: string;
          let isStarting: boolean;
          let isLoading: boolean;

          switch (updated.status) {
            case 'preparing':
              statusMessage = 'Preparando transmisión…';
              isStarting = true;
              isLoading = true;
              break;
            case 'connecting':
              statusMessage = 'Esperando señal del locutor…';
              isStarting = true;
              isLoading = true;
              break;
            case 'live':
              statusMessage = 'EN VIVO';
              isStarting = false;
              isLoading = false;
              break;
            case 'reconnecting':
              statusMessage = 'Reconectando señal…';
              isStarting = false;
              isLoading = false;
              break;
            case 'ending':
              statusMessage = 'Finalizando programa…';
              isStarting = false;
              isLoading = false;
              break;
            default:
              statusMessage = LIVE_SESSION_STATUS_LABELS[updated.status];
              isStarting = false;
              isLoading = false;
          }

          setLiveSessionStates((prev) => ({
            ...prev,
            [s.liveId]: {
              sessionId: s.sessionId,
              status: updated.status,
              statusMessage,
              errorMessage: updated.status === 'failed' ? (updated.failure_reason || null) : null,
              isStarting,
              durationSeconds: updated.duration_seconds ?? null,
              isLoading,
              startedAt: updated.started_at ?? s.startedAt,
            },
          }));

          if (updated.status === 'live') {
            setLives((prev) =>
              prev.map((l) =>
                l.id === s.liveId ? { ...l, status: 'en_vivo' as LiveStatus } : l
              )
            );
          }

          // Reanudar polling para esta sesión
          const sessionId = s.sessionId;
          const liveId = s.liveId;
          let hasReachedLive = updated.status === 'live';

          const poll = setInterval(async () => {
            try {
              const polled = await liveSessionService.getLiveSession(sessionId);

              setLiveSessionStates((prev) => {
                const cur = prev[liveId];
                if (!cur) return prev;

                let msg: string;
                let starting: boolean;
                let loading: boolean;

                switch (polled.status) {
                  case 'preparing':
                    msg = 'Preparando transmisión…';
                    starting = true;
                    loading = true;
                    break;
                  case 'connecting':
                    msg = 'Esperando señal del locutor…';
                    starting = true;
                    loading = true;
                    break;
                  case 'live':
                    msg = 'EN VIVO';
                    starting = false;
                    loading = false;
                    break;
                  case 'reconnecting':
                    msg = 'Reconectando señal…';
                    starting = false;
                    loading = false;
                    break;
                  case 'ending':
                    msg = 'Finalizando programa…';
                    starting = false;
                    loading = false;
                    break;
                  case 'completed':
                    msg = 'Programa finalizado. AutoDJ nuevamente al aire.';
                    starting = false;
                    loading = false;
                    break;
                  case 'failed':
                    msg = polled.failure_reason || 'La transmisión falló.';
                    starting = false;
                    loading = false;
                    break;
                  default:
                    msg = LIVE_SESSION_STATUS_LABELS[polled.status];
                    starting = false;
                    loading = false;
                }

                return {
                  ...prev,
                  [liveId]: {
                    ...cur,
                    status: polled.status,
                    statusMessage: msg,
                    errorMessage: polled.status === 'failed' ? (polled.failure_reason || 'Error desconocido.') : null,
                    isStarting: starting,
                    durationSeconds: polled.duration_seconds ?? cur.durationSeconds,
                    isLoading: loading,
                    startedAt: polled.started_at ?? cur.startedAt,
                  },
                };
              });

              if (polled.status === 'live') {
                setLives((prev) =>
                  prev.map((l) =>
                    l.id === liveId ? { ...l, status: 'en_vivo' as LiveStatus } : l
                  )
                );
                if (!hasReachedLive) {
                  hasReachedLive = true;
                  SwalAlert.toast('Live restaurado.', 'success');
                }
                return;
              }

              if (polled.status === 'reconnecting' || polled.status === 'ending') {
                return;
              }

              // Terminal
              if (polled.status === 'completed' || polled.status === 'failed') {
                clearInterval(poll);
                delete pollingRefs.current[sessionId];
                removeStoredSession(liveId);
                if (polled.status === 'completed') {
                  setLives((prev) =>
                    prev.map((l) =>
                      l.id === liveId ? { ...l, status: 'finalizado' as LiveStatus } : l
                    )
                  );
                }
                return;
              }
            } catch {
              // Silently retry
            }
          }, 3000);

          pollingRefs.current[sessionId] = poll;
        } catch {
          // Si no se puede restaurar, limpiar localStorage
          removeStoredSession(s.liveId);
        }
      }
    })();
  }, [pageState]);

  // Simular carga
  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.06;
      if (shouldFail) {
        setPageState('error');
      } else {
        setLives(adminLiveData.lives);
        setPageState('ready');
      }
    }, 800 + Math.random() * 800);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.04;
      if (shouldFail) {
        setPageState('error');
      } else {
        setLives(adminLiveData.lives);
        setPageState('ready');
      }
    }, 500 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  /* ─── Filtrado ─── */
  const filteredLives = useMemo(() => {
    return lives.filter((l) => {
      const matchStatus = filterStatus === 'todos' || l.status === filterStatus;
      const matchProvider = filterProvider === 'todos' || l.provider === filterProvider;
      const matchSearch =
        !searchQuery ||
        l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.host.toLowerCase().includes(searchQuery.toLowerCase());
      return matchStatus && matchProvider && matchSearch;
    });
  }, [lives, filterStatus, filterProvider, searchQuery]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const enVivo = lives.filter((l) => l.status === 'en_vivo').length;
    const programados = lives.filter((l) => l.status === 'scheduled' || l.status === 'draft').length;
    const finalizados = lives.filter((l) => l.status === 'finalizado' || l.status === 'recording_available' || l.status === 'published' || l.status === 'archived').length;
    const total = lives.length;
    return { enVivo, programados, finalizados, total };
  }, [lives]);

  /* ─── Handlers ─── */
  const handleCreate = () => {
    if (!isPremium) { showBlockAlert(openUpgradeModal); return; }
    setEditingLive(null);
    setFormModalOpen(true);
  };

  const handleEdit = (live: AdminLive) => {
    if (!isPremium) { showBlockAlert(openUpgradeModal); return; }
    setEditingLive(live);
    setFormModalOpen(true);
  };

  const handleSave = (data: {
    title: string;
    description: string;
    date: string;
    time: string;
    host: string;
    provider: LiveProvider;
    providerUrl: string;
    status: LiveStatus;
  }) => {
    if (editingLive) {
      setLives((prev) =>
        prev.map((l) =>
          l.id === editingLive.id
            ? { ...l, ...data }
            : l
        )
      );
    } else {
      const newLive: AdminLive = {
        id: Math.max(...lives.map((l) => l.id), 0) + 1,
        title: data.title,
        description: data.description,
        date: data.date,
        time: data.time,
        host: data.host,
        imageUrl:
          'https://readdy.ai/api/search-image?query=Professional%20live%20streaming%20broadcast%20studio%20setup%20with%20on%20air%20sign%2C%20modern%20equipment%20and%20lighting%2C%20ready%20to%20go%20live%20atmosphere%2C%20empty%20studio%20waiting%20for%20host%2C%20broadcast%20production%20environment&width=1280&height=720&seq=live-placeholder-new&orientation=landscape',
        provider: data.provider,
        providerUrl: data.providerUrl,
        status: data.status,
        createdAt: new Date().toISOString(),
      };
      setLives((prev) => [newLive, ...prev]);
    }
    setFormModalOpen(false);
    setEditingLive(null);
    SwalAlert.toast(editingLive ? 'Live actualizado.' : 'Live creado exitosamente.', 'success');
  };

  const handleDelete = (live: AdminLive) => {
    SwalAlert.confirm(
      '¿Eliminar live?',
      `Se eliminará permanentemente "${escapeHtml(live.title)}". Esta acción no se puede deshacer.`,
    ).then((result) => {
        if (result.isConfirmed) {
          setLives((prev) => prev.filter((l) => l.id !== live.id));
          SwalAlert.toast('Live eliminado.', 'success');
        }
      });
  };

  const handleFinalizar = useCallback(async (live: AdminLive) => {
    const confirmResult = await SwalAlert.confirm(
      '¿Finalizar transmisión?',
      `Se finalizará la transmisión "${escapeHtml(live.title)}". La reproducción volverá al AutoDJ.`,
    );

    if (!confirmResult.isConfirmed) return;

    const liveId = live.id;
    const session = liveSessionStates[liveId];

    // ── Flujo real con backend ──
    if (session?.sessionId) {
      const sessionId = session.sessionId;

      // Marcar como "ending" inmediatamente
      setLiveSessionStates((prev) => ({
        ...prev,
        [liveId]: {
          ...prev[liveId],
          status: 'ending',
          statusMessage: 'Finalizando programa…',
          errorMessage: null,
          isStarting: false,
          isLoading: false,
        },
      }));

      try {
        await liveSessionService.finishLiveSession(sessionId);

        // Limpiar polling anterior (del flujo de inicio) para evitar duplicados
        if (pollingRefs.current[sessionId]) {
          clearInterval(pollingRefs.current[sessionId]);
          delete pollingRefs.current[sessionId];
        }

        // Polling para ending → completed/failed
        const poll = setInterval(async () => {
          try {
            const updated = await liveSessionService.getLiveSession(sessionId);

            setLiveSessionStates((prev) => {
              const cur = prev[liveId];
              if (!cur) return prev;

              let statusMessage: string;
              if (updated.status === 'completed') {
                statusMessage = 'Programa finalizado. AutoDJ nuevamente al aire.';
              } else if (updated.status === 'failed') {
                statusMessage = updated.failure_reason || 'Error al finalizar.';
              } else if (updated.status === 'ending') {
                statusMessage = 'Finalizando programa…';
              } else {
                statusMessage = LIVE_SESSION_STATUS_LABELS[updated.status];
              }

              return {
                ...prev,
                [liveId]: {
                  ...cur,
                  status: updated.status,
                  statusMessage,
                  errorMessage: updated.status === 'failed' ? (updated.failure_reason || 'Error al finalizar la transmisión.') : null,
                  isStarting: false,
                  isLoading: false,
                  durationSeconds: updated.duration_seconds ?? cur.durationSeconds,
                  startedAt: updated.started_at ?? cur.startedAt,
                },
              };
            });

            if (updated.status === 'completed') {
              clearInterval(poll);
              delete pollingRefs.current[sessionId];
              removeStoredSession(liveId);
              setLives((prev) =>
                prev.map((l) =>
                  l.id === liveId ? { ...l, status: 'finalizado' as LiveStatus } : l
                )
              );
              SwalAlert.toast('Programa finalizado. AutoDJ nuevamente al aire. Cabina liberada para el siguiente locutor.', 'success');
              setPostLiveModal({ open: true, live: { ...live, status: 'finalizado' } });
              return;
            }

            if (updated.status === 'failed') {
              clearInterval(poll);
              delete pollingRefs.current[sessionId];
              removeStoredSession(liveId);
              const reason = updated.failure_reason || 'No se pudo finalizar la transmisión.';
              const retryResult = await SwalAlert.confirm(
                'Error al finalizar',
                `${reason}\n\n¿Querés reintentar la finalización?`,
              );
              if (retryResult.isConfirmed) {
                handleFinalizar(live);
              }
              return;
            }
          } catch (pollErr) {
            console.error('Finish polling error', sessionId, pollErr);
          }
        }, 3000);

        pollingRefs.current[sessionId] = poll;
      } catch (err: unknown) {
        // ── La API falló → la transmisión sigue viva, revertir estado ──
        const message = err instanceof Error ? err.message : 'Error al finalizar la transmisión.';
        setLiveSessionStates((prev) => {
          const cur = prev[liveId];
          if (!cur) return prev;
          return {
            ...prev,
            [liveId]: {
              ...cur,
              status: 'live',
              statusMessage: 'EN VIVO',
              errorMessage: null,
              isStarting: false,
              isLoading: false,
            },
          };
        });
        // Preguntar si quiere reintentar
        const retryResult = await SwalAlert.confirm(
          'Error al finalizar',
          `${message}\n\n¿Querés reintentar la finalización?`,
        );
        if (retryResult.isConfirmed) {
          handleFinalizar(live);
        }
      }
      return;
    }

    // ── Flujo legacy (sin sesión activa) ──
    setLives((prev) =>
      prev.map((l) =>
        l.id === liveId ? { ...l, status: 'finalizado' as LiveStatus } : l
      )
    );
    setPostLiveModal({ open: true, live: { ...live, status: 'finalizado' } });
  }, [liveSessionStates]);

  const handlePostLiveAction = (result: PostLiveFlowResult) => {
    const live = postLiveModal.live;
    if (!live) return;

    switch (result.action) {
      case 'recording':
        if (result.recording) {
          setLives((prev) =>
            prev.map((l) =>
              l.id === live.id ? { ...l, status: 'recording_available' as LiveStatus, generatedRecordingId: result.recording!.id } : l
            )
          );
          SwalAlert.toast('Registro de grabación creado.', 'success');
        }
        break;

      case 'video': {
        const videoId = Math.max(...(adminLiveData.lives.map((l) => l.id) || [200]), 0) + Math.floor(Math.random() * 100);
        setLives((prev) =>
          prev.map((l) =>
            l.id === live.id ? { ...l, status: 'published' as LiveStatus, generatedVideoId: videoId } : l
          )
        );
        SwalAlert.success(
          'Video creado',
          `Se creó el video "${result.videoData?.title || live.title}" y se vinculó al live. Podés verlo en la sección de Videos.`
        );
        break;
      }

      case 'post': {
        const postId = Math.max(...(adminLiveData.lives.map((l) => l.id) || [200]), 0) + Math.floor(Math.random() * 200);
        setLives((prev) =>
          prev.map((l) =>
            l.id === live.id ? { ...l, generatedPostId: postId } : l
          )
        );
        SwalAlert.success(
          'Publicación creada',
          `Se creó la publicación "${result.postData?.title || live.title}". Podés verla en la sección de Publicaciones.`
        );
        break;
      }

      case 'archive':
        setLives((prev) =>
          prev.map((l) =>
            l.id === live.id ? { ...l, status: 'archived' as LiveStatus } : l
          )
        );
        SwalAlert.toast('Live archivado.', 'info');
        break;

      case 'notify':
        if (result.notifyContext) {
          setFlowModal({ open: true, context: result.notifyContext });
        }
        break;
    }

    setPostLiveModal({ open: false, live: null });
  };

  // ═══════════════════════════════════════════════
  // INICIAR LIVE — Flujo real con backend PHP
  // ═══════════════════════════════════════════════

  const handleIniciar = useCallback(async (live: AdminLive) => {
    if (!isPremium) { showBlockAlert(openUpgradeModal); return; }

    // 1. Pedir confirmación
    const confirmResult = await SwalAlert.confirm(
      '¿Iniciar transmisión?',
      `¿Confirmas que deseas tomar la transmisión en vivo para "${escapeHtml(live.title)}"?`,
    );

    if (!confirmResult.isConfirmed) return;

    const liveId = live.id;

    // 2. Bloquear dobles clics — marcar como "iniciando" con overlay completo
    setLiveSessionStates((prev) => ({
      ...prev,
      [liveId]: {
        sessionId: '',
        status: 'preparing',
        statusMessage: 'Preparando transmisión…',
        errorMessage: null,
        isStarting: true,
        durationSeconds: null,
        isLoading: true,
        startedAt: null,
      },
    }));

    try {
      // 3. Obtener contexto de radio (radio_id + station_id)
      const ctx = await getAdminRadioContext();
      if (!ctx) {
        throw new Error('No se pudo identificar tu radio. Asegurate de tener una radio y estación configuradas.');
      }

      // 4. Llamar al endpoint real de inicio
      const session = await liveSessionService.startLiveSession({
        station_id: ctx.stationId,
        radio_id: ctx.radioId,
      });

      // 5. Guardar sessionId real + persistir para recuperación tras refresh
      const sessionId = session.id;
      const now = new Date().toISOString();
      persistSession(liveId, { sessionId, liveId, startedAt: now });
      setLiveSessionStates((prev) => ({
        ...prev,
        [liveId]: {
          sessionId,
          status: session.status,
          statusMessage: LIVE_SESSION_STATUS_LABELS[session.status],
          errorMessage: null,
          isStarting: true,
          durationSeconds: session.duration_seconds ?? null,
          isLoading: true,
          startedAt: session.started_at ?? now,
        },
      }));

      // 6. Iniciar consulta del estado real (polling cada 3s)
      //    CONTINÚA durante 'live' para detectar 'reconnecting' y 'ending'.
      //    Solo se detiene en 'completed', 'failed' o por cleanup externo.
      let hasReachedLive = false;

      const poll = setInterval(async () => {
        try {
          const updated = await liveSessionService.getLiveSession(sessionId);

          setLiveSessionStates((prev) => {
            const current = prev[liveId];
            if (!current) return prev;

            let statusMessage: string;
            let isStarting: boolean;
            let isLoading: boolean;

            switch (updated.status) {
              case 'preparing':
                statusMessage = 'Preparando transmisión…';
                isStarting = true;
                isLoading = true;
                break;
              case 'connecting':
                statusMessage = 'Esperando señal del locutor…';
                isStarting = true;
                isLoading = true;
                break;
              case 'live':
                statusMessage = 'EN VIVO';
                isStarting = false;
                isLoading = false;
                break;
              case 'reconnecting':
                statusMessage = 'Reconectando señal…';
                isStarting = false;
                isLoading = false;
                break;
              case 'ending':
                statusMessage = 'Finalizando programa…';
                isStarting = false;
                isLoading = false;
                break;
              case 'completed':
                statusMessage = 'Programa finalizado. AutoDJ nuevamente al aire.';
                isStarting = false;
                isLoading = false;
                break;
              case 'failed':
                statusMessage = updated.failure_reason || 'La transmisión falló.';
                isStarting = false;
                isLoading = false;
                break;
              default:
                statusMessage = LIVE_SESSION_STATUS_LABELS[updated.status];
                isStarting = false;
                isLoading = false;
            }

            return {
              ...prev,
              [liveId]: {
                ...current,
                status: updated.status,
                statusMessage,
                errorMessage: updated.status === 'failed' ? (updated.failure_reason || 'Error desconocido.') : null,
                isStarting,
                durationSeconds: updated.duration_seconds ?? current.durationSeconds,
                isLoading,
                startedAt: updated.started_at ?? current.startedAt,
              },
            };
          });

          // ── Side effects por estado ──

          if (updated.status === 'live') {
            setLives((prev) =>
              prev.map((l) =>
                l.id === liveId ? { ...l, status: 'en_vivo' as LiveStatus } : l
              )
            );

            // Solo mostrar toast + oferta de notificación la primera vez
            if (!hasReachedLive) {
              hasReachedLive = true;
              SwalAlert.toast('¡Live iniciado!', 'success');

              SwalAlert.confirm(
                '¿Notificar a tus oyentes?',
                `¿Querés enviar una notificación push avisando que "${live.title}" está en vivo?`,
              ).then((notifyResult) => {
                if (notifyResult.isConfirmed) {
                  setFlowModal({
                    open: true,
                    context: {
                      type: 'transmision',
                      relatedModule: 'live',
                      relatedItemId: live.id,
                      relatedItemName: live.title,
                      prefilledTitle: `¡Estamos en vivo! ${live.title}`,
                      prefilledMessage: `${live.host} está transmitiendo "${live.title}" en vivo ahora. ¡Conectate y no te lo pierdas!`,
                    },
                  });
                }
              });
            }
            // NO detenemos el polling — seguimos monitoreando por reconnecting/ending
            return;
          }

          if (updated.status === 'reconnecting') {
            // El live sigue 'en_vivo', el badge se actualiza vía liveSessionStates
            return;
          }

          if (updated.status === 'ending') {
            // Transición a finalización — probablemente disparada por el backend
            return;
          }

          // ── Estados terminales — DETENER POLLING + LIMPIAR ──

          if (updated.status === 'completed') {
            clearInterval(poll);
            delete pollingRefs.current[sessionId];
            removeStoredSession(liveId);
            setLives((prev) =>
              prev.map((l) =>
                l.id === liveId
                  ? { ...l, status: 'finalizado' as LiveStatus }
                  : l
              )
            );
            return;
          }

          if (updated.status === 'failed') {
            clearInterval(poll);
            delete pollingRefs.current[sessionId];
            removeStoredSession(liveId);
            const reason = updated.failure_reason || 'La transmisión falló.';
            SwalAlert.error('Error en la transmisión', reason);
            return;
          }
        } catch (pollErr) {
          // Silently retry on polling errors — no romper el flujo
          console.error('Polling error for session', sessionId, pollErr);
        }
      }, 3000);

      pollingRefs.current[sessionId] = poll;

    } catch (err: unknown) {
      // Error al iniciar — limpiar estado y mostrar error
      const message = err instanceof Error ? err.message : 'Error desconocido al iniciar la transmisión.';

      removeStoredSession(liveId);
      setLiveSessionStates((prev) => ({
        ...prev,
        [liveId]: {
          sessionId: '',
          status: 'failed',
          statusMessage: 'Falló',
          errorMessage: message,
          isStarting: false,
          durationSeconds: null,
          isLoading: false,
          startedAt: null,
        },
      }));

      SwalAlert.error('Error al iniciar', message);
    }
  }, [isPremium, openUpgradeModal, getAdminRadioContext]);

  const handleCopyLink = (live: AdminLive) => {
    const link = `https://radiofrecuenciaweb.com/live/${live.id}`;
    navigator.clipboard.writeText(link).then(() => {
      SwalAlert.toast('Link copiado al portapapeles.', 'success');
    }).catch(() => {
      SwalAlert.toast('Error al copiar el link.', 'error');
    });
  };

  /* ─── Loading Skeleton ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-40 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="w-16 h-3 rounded bg-background-200 animate-pulse mb-2"></div>
                    <div className="w-10 h-7 rounded bg-background-200 animate-pulse mb-1"></div>
                    <div className="w-20 h-2 rounded bg-background-200 animate-pulse"></div>
                  </div>
                ))}
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <div className="w-40 h-4 rounded bg-background-200 animate-pulse mx-auto mb-3"></div>
                <div className="w-60 h-3 rounded bg-background-200 animate-pulse mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="aspect-video w-full rounded-lg bg-background-200 animate-pulse mb-3"></div>
                    <div className="w-3/4 h-4 rounded bg-background-200 animate-pulse mb-2"></div>
                    <div className="w-full h-3 rounded bg-background-200 animate-pulse mb-1"></div>
                    <div className="w-1/2 h-3 rounded bg-background-200 animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Error State ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-live-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">
                Error cargando lives
              </h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se pudieron cargar las transmisiones. Verificá tu conexión e intentá de nuevo.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-live-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Lives</h1>
              <p className="text-foreground-400 text-[9px]">
                {stats.enVivo > 0 && (
                  <span className="text-red-500 font-semibold">{stats.enVivo} en vivo</span>
                )}
                {stats.enVivo === 0 && `${stats.total} lives`}
                {stats.programados > 0 && ` &bull; ${stats.programados} programados`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Link
              to="/admin/videos"
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Videos
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
            >
              <i className="ri-arrow-left-line text-xs"></i>
            </Link>
          </div>
        </div>
      </header>


          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Transmisiones en Vivo</h2>
                <p className="text-foreground-400 text-xs">
                  {stats.total} lives &bull; {stats.programados} programados &bull; {stats.finalizados} finalizados
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Link
                  to="/admin/videos"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-video-line text-xs mr-1"></i>
                  Videos
                </Link>
                <Link
                  to="/admin/dashboard"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-dashboard-line text-xs mr-1"></i>
                  Dashboard
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreate}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-background-50 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  isPremium ? 'bg-red-500 hover:bg-red-600' : 'bg-foreground-300 hover:bg-foreground-400'
                }`}
              >
                {isPremium ? <i className="ri-add-line text-sm"></i> : <i className="ri-lock-line text-sm"></i>}
                Crear live
              </button>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats mini cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Lives</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.total}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">Total creados</p>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">En vivo</p>
                <p className="text-2xl font-bold text-red-500 mt-1">
                  {stats.enVivo > 0 && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block mr-1.5 align-middle"></span>}
                  {stats.enVivo}
                </p>
                <p className="text-[10px] text-foreground-400 mt-0.5">Transmitiendo ahora</p>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Programados</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.programados}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span className="text-[10px] text-foreground-400">Próximos</span>
                </div>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Proveedores</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">4</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <i className="ri-youtube-fill text-red-500 text-[10px]"></i>
                  <i className="ri-facebook-circle-fill text-blue-500 text-[10px]"></i>
                  <i className="ri-server-line text-emerald-500 text-[10px]"></i>
                  <i className="ri-live-line text-violet-500 text-[10px]"></i>
                </div>
              </div>
            </div>

            {/* Quick action bar */}
            <div className={`bg-background-50 rounded-2xl border overflow-hidden ${
              isPremium ? 'border-background-200' : 'border-amber-300/60 bg-amber-50/30'
            }`}>
              <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-foreground-900">
                    {isPremium ? 'Crear nueva transmisión' : 'Lives — Exclusivo Premium'}
                  </p>
                  <p className="text-[11px] text-foreground-500 mt-0.5">
                    {isPremium
                      ? 'Programá un live con YouTube, Facebook, VPS propio o Mux'
                      : 'Actualizá a Premium para crear y gestionar transmisiones en vivo'}
                  </p>
                </div>
                <button
                  onClick={handleCreate}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    isPremium
                      ? 'bg-red-500 text-background-50 hover:bg-red-600'
                      : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  }`}
                >
                  <i className={isPremium ? 'ri-add-line' : 'ri-lock-line'}></i>
                  {isPremium ? 'Crear live' : 'Premium requerido'}
                </button>
              </div>

              {!isPremium && (
                <div className="border-t border-amber-200/60 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-amber-50/80">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-vip-diamond-fill text-accent-600 text-sm"></i>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      <strong className="text-accent-700">Plan Premium</strong> desbloquea lives ilimitados, videos, analytics avanzado y 500 GB de storage.
                    </p>
                  </div>
                  <button
                    onClick={openUpgradeModal}
                    className="shrink-0 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-semibold hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap ml-auto"
                  >
                    Actualizar a Premium
                  </button>
                </div>
              )}
            </div>

            {/* Live Grid */}
            <div>
              <LiveGrid
                lives={filteredLives}
                filterStatus={filterStatus}
                onChangeStatus={setFilterStatus}
                filterProvider={filterProvider}
                onChangeProvider={setFilterProvider}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onFinalizar={handleFinalizar}
                onCopyLink={handleCopyLink}
                onIniciar={handleIniciar}
                isPremium={isPremium}
                liveSessionStates={liveSessionStates}
              />
            </div>
          </div>




      {/* Live Form Modal */}
      <LiveFormModal
        open={formModalOpen}
        live={editingLive}
        providers={allProviders}
        onClose={() => {
          setFormModalOpen(false);
          setEditingLive(null);
        }}
        onSave={handleSave}
      />
      <NotificationFlowModal open={flowModal.open} context={flowModal.context} onClose={() => setFlowModal((prev) => ({ ...prev, open: false }))} />

      <PostLiveFlowModal
        open={postLiveModal.open}
        live={postLiveModal.live!}
        recording={postLiveModal.live ? getRecordingByLiveId(postLiveModal.live.id) : undefined}
        onClose={() => setPostLiveModal({ open: false, live: null })}
        onAction={handlePostLiveAction}
      />
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}